import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import type { CheckoutSessionData } from "../types/checkout";
import {
  createDirectEstiaIntent,
  DIRECT_FLOW_CONFIG,
  hasDirectFlowConfig,
} from "../services/estiaDirectFlow";
import { buildEstiaPaymentDeepLink } from "../services/paymentLinks";
import { isMobileBrowser } from "../services/openEstiaApp";
import { isEmbeddedCheckout } from "../utils/embedMode";
import {
  notifyParentCheckoutClosed,
  notifyParentCheckoutReady,
  notifyParentPaymentFailed,
  notifyParentPaymentSuccess,
  type PaymentResultPayload,
} from "../utils/parentMessaging";
import { resolveSessionViewState } from "../utils/resolveViewState";
import { buildTransactionId } from "../utils/buildTransactionId";

const POLL_MS = 3000;
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "/api/v1";

const DEMO_SESSION: CheckoutSessionData = {
  sessionId: "demo-session",
  merchantName: "Estia Payments",
  amount: 98,
  currency: "EUR",
  status: "PENDING",
  expiresAt: new Date(Date.now() + 45_000).toISOString(),
};

export function useCheckoutSession() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<CheckoutSessionData | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(45);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");

  const readyNotifiedRef = useRef(false);
  const resultNotifiedRef = useRef(false);

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.get("sessionId") || params.get("session_id");
  }, []);

  const selectedMerchantName = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.get("merchantName") || "Estia Payments";
  }, []);

  const directAmount = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const urlAmount = Number(params.get("amount") || "0");
    return urlAmount > 0 ? urlAmount : DIRECT_FLOW_CONFIG.defaultAmount;
  }, []);

  const directTransactionId = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.get("transactionId") || params.get("orderId") || buildTransactionId();
  }, []);

  const viewState = useMemo(
    () => resolveSessionViewState(sessionData?.status, timeRemaining),
    [sessionData?.status, timeRemaining]
  );

  useEffect(() => {
    async function buildQr(id: string) {
      const qr = await QRCode.toDataURL(buildEstiaPaymentDeepLink(id), {
        margin: 1,
        width: 320,
      });
      setQrDataUrl(qr);
    }

    async function fetchCheckout() {
      try {
        if (!sessionId) {
          if (hasDirectFlowConfig()) {
            const { intentId } = await createDirectEstiaIntent(directAmount, directTransactionId);
            const expiresAt = new Date(
              Date.now() + DIRECT_FLOW_CONFIG.expirySeconds * 1000
            ).toISOString();

            setSessionData({
              sessionId: intentId,
              merchantName: selectedMerchantName,
              amount: directAmount,
              currency: DIRECT_FLOW_CONFIG.currency,
              status: "PENDING",
              expiresAt,
            });
            setTimeRemaining(DIRECT_FLOW_CONFIG.expirySeconds);
            await buildQr(intentId);
            return;
          }

          setSessionData(DEMO_SESSION);
          setTimeRemaining(45);
          await buildQr(DEMO_SESSION.sessionId);
          return;
        }

        const checkoutRes = await fetch(`${API_BASE_URL}/checkout/${sessionId}`);
        if (!checkoutRes.ok) {
          throw new Error(`Unable to load checkout (${checkoutRes.status})`);
        }
        const checkout = (await checkoutRes.json()) as CheckoutSessionData;

        setSessionData({
          sessionId: checkout.sessionId || sessionId,
          merchantName: checkout.merchantName || "Estia Payments",
          amount: Number(checkout.amount) || 0,
          currency: checkout.currency || "EUR",
          orderId: checkout.orderId,
          status: checkout.status || "PENDING",
          expiresAt: checkout.expiresAt,
        });

        await buildQr(checkout.sessionId || sessionId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load checkout.");
      } finally {
        setLoading(false);
      }
    }

    void fetchCheckout();
  }, [directAmount, directTransactionId, selectedMerchantName, sessionId]);

  useEffect(() => {
    if (!sessionData?.expiresAt) return;

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(sessionData.expiresAt as string).getTime() - Date.now()) / 1000)
      );
      setTimeRemaining(remaining);
    };

    tick();
    const timer = globalThis.setInterval(tick, 1000);
    return () => globalThis.clearInterval(timer);
  }, [sessionData?.expiresAt]);

  const refreshStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const statusRes = await fetch(`${API_BASE_URL}/sessions/${sessionId}/status`);
      if (!statusRes.ok) return;
      const statusData = (await statusRes.json()) as { status?: string };
      if (statusData.status) {
        setSessionData((prev) => (prev ? { ...prev, status: statusData.status } : prev));
      }
    } catch {
      // Keep current UI state without flicker on polling errors.
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (viewState === "SUCCESS" || viewState === "FAILED" || viewState === "TIMEOUT") return;

    const poll = globalThis.setInterval(refreshStatus, POLL_MS);

    // Mobile only: browsers suspend timers while the Estia app is in the
    // foreground (after "Open Estia app"), so re-check as soon as the checkout
    // is visible again instead of waiting for the next interval tick. Desktop
    // keeps its existing foreground polling behavior.
    const resumeListeners: Array<() => void> = [];
    if (isMobileBrowser()) {
      const onResume = () => {
        if (globalThis.document?.visibilityState === "visible") {
          void refreshStatus();
        }
      };

      globalThis.document?.addEventListener("visibilitychange", onResume);
      globalThis.addEventListener("focus", onResume);
      globalThis.addEventListener("pageshow", onResume);

      resumeListeners.push(() => {
        globalThis.document?.removeEventListener("visibilitychange", onResume);
        globalThis.removeEventListener("focus", onResume);
        globalThis.removeEventListener("pageshow", onResume);
      });
    }

    return () => {
      globalThis.clearInterval(poll);
      resumeListeners.forEach((remove) => remove());
    };
  }, [sessionId, viewState, refreshStatus]);

  useEffect(() => {
    if (readyNotifiedRef.current || !sessionData?.sessionId) return;
    if (!isEmbeddedCheckout() || !isMobileBrowser()) return;

    readyNotifiedRef.current = true;
    notifyParentCheckoutReady(sessionData.sessionId);
  }, [sessionData?.sessionId]);

  useEffect(() => {
    if (resultNotifiedRef.current || !sessionData?.sessionId) return;
    if (!isEmbeddedCheckout() || !isMobileBrowser()) return;
    if (viewState !== "SUCCESS" && viewState !== "FAILED") return;

    resultNotifiedRef.current = true;

    const payload: PaymentResultPayload = {
      sessionId: sessionData.sessionId,
      ...(sessionData.orderId ? { orderId: sessionData.orderId } : {}),
      amount: sessionData.amount,
      currency: sessionData.currency,
    };

    if (viewState === "SUCCESS") {
      notifyParentPaymentSuccess(sessionData.sessionId, payload);
    } else {
      notifyParentPaymentFailed(sessionData.sessionId, payload);
    }
  }, [sessionData, viewState]);

  const close = () => {
    if (isEmbeddedCheckout() && sessionId) {
      notifyParentCheckoutClosed(sessionId);
      return;
    }

    globalThis.close();
  };

  const refreshQr = () => {
    globalThis.location.reload();
  };

  return {
    loading,
    error,
    sessionData,
    viewState,
    timeRemaining,
    qrDataUrl,
    close,
    refreshQr,
  };
}
